import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

// Mirrors Wheels.ERP.Shared.Accounting report DTOs.

export interface TrialBalanceRowDto {
  accountId: string;
  code: string;
  nameAr: string;
  nameEn: string | null;
  accountType: number;
  normalBalance: number;
  totalDebit: number;
  totalCredit: number;
  balance: number;
}

export interface TrialBalanceDto {
  asOf: string;
  totalDebit: number;
  totalCredit: number;
  rows: TrialBalanceRowDto[];
}

export interface AccountLedgerRowDto {
  journalEntryId: string;
  entryNumber: string;
  entryDate: string;
  source: number;
  description: string | null;
  memo: string | null;
  debit: number;
  credit: number;
  runningBalance: number;
}

export interface AccountLedgerDto {
  accountId: string;
  code: string;
  nameAr: string;
  nameEn: string | null;
  from: string;
  to: string;
  openingBalance: number;
  closingBalance: number;
  rows: AccountLedgerRowDto[];
}

export interface IncomeStatementLineDto {
  accountId: string;
  code: string;
  nameAr: string;
  nameEn: string | null;
  amount: number;
}

export interface IncomeStatementDto {
  from: string;
  to: string;
  branchId: string | null;
  totalRevenue: number;
  totalExpenses: number;
  netIncome: number;
  revenue: IncomeStatementLineDto[];
  expenses: IncomeStatementLineDto[];
}

export function useTrialBalance(asOf: string) {
  return useQuery({
    queryKey: ['acct-reports', 'trial-balance', asOf] as const,
    queryFn: async () =>
      (
        await apiClient.get<TrialBalanceDto>('/api/accounting/reports/trial-balance', {
          params: { asOf },
        })
      ).data,
  });
}

export function useAccountLedger(accountId: string | null, from: string, to: string) {
  return useQuery({
    queryKey: ['acct-reports', 'ledger', accountId, from, to] as const,
    enabled: accountId !== null,
    queryFn: async () =>
      (
        await apiClient.get<AccountLedgerDto>(
          `/api/accounting/reports/ledger/${accountId}`,
          { params: { from, to } },
        )
      ).data,
  });
}

export function useIncomeStatement(from: string, to: string, branchId: string | null) {
  return useQuery({
    queryKey: ['acct-reports', 'income-statement', from, to, branchId] as const,
    queryFn: async () =>
      (
        await apiClient.get<IncomeStatementDto>('/api/accounting/reports/income-statement', {
          params: { from, to, branchId: branchId ?? undefined },
        })
      ).data,
  });
}

export interface AgingRowDto {
  partyId: string;
  partyName: string;
  current: number;
  days31To60: number;
  days61To90: number;
  over90: number;
  total: number;
}

export interface AgingReportDto {
  kind: number;
  asOf: string;
  total: number;
  rows: AgingRowDto[];
}

export function useAging(kind: 1 | 2, asOf: string) {
  return useQuery({
    queryKey: ['acct-reports', 'aging', kind, asOf] as const,
    queryFn: async () =>
      (
        await apiClient.get<AgingReportDto>('/api/accounting/reports/aging', {
          params: { kind, asOf },
        })
      ).data,
  });
}

export async function downloadAccountingExcel(
  slug: 'trial-balance' | 'income-statement',
  params: Record<string, string>,
): Promise<void> {
  const response = await apiClient.get<Blob>(`/api/accounting/reports/${slug}/excel`, {
    params,
    responseType: 'blob',
  });
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${slug}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
